import React, { useEffect, useState, useCallback } from "react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Users, Boxes, Wallet, Download, TrendingUp, Store } from "lucide-react";
import api from "../lib/api";
import { Modal, Select, Button } from "../lib/ui";
import { fmtRp, fmtNum, fmtUnit } from "../lib/constants";
import { useT } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";

const monthAgo = () => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);
const shortDate = (d) => (d || "").slice(5);

export default function EmployeeMealReport({ open, onClose }) {
  const { t } = useT();
  const { isRole } = useAuth();
  const isBranch = isRole("BRANCH_STAFF");
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [start, setStart] = useState(monthAgo());
  const [end, setEnd] = useState(today());
  const [data, setData] = useState(null);

  const params = useCallback(() => {
    const p = new URLSearchParams();
    if (branchId) p.set("branch_id", branchId);
    if (start) p.set("start", start);
    if (end) p.set("end", end);
    return p.toString();
  }, [branchId, start, end]);

  const load = useCallback(() => {
    setData(null);
    api.get(`/employee-meals/summary?${params()}`).then(({ data }) => setData(data)).catch(() => setData({ products: [], branch_totals: [], trend: [], summary: {} }));
  }, [params]);

  useEffect(() => { if (open && !isBranch) api.get("/branches").then(({ data }) => setBranches(data)); }, [open, isBranch]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const exportFile = async (kind) => {
    const res = await api.get(`/employee-meals/export.${kind}?${params()}`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(res.data);
    a.download = `employee-meals.${kind}`;
    a.click();
  };

  const s = data?.summary || {};

  return (
    <Modal open={open} onClose={onClose} size="lg" title={t("Employee Meal Report")}>
      <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-4" data-testid="emeal-report-filters">
        {!isBranch && (
          <Select value={branchId} onChange={(e) => setBranchId(e.target.value)} data-testid="emeal-report-branch">
            <option value="">{t("All Branches")}</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Select>
        )}
        <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="cf-input" data-testid="emeal-report-start" />
        <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="cf-input" data-testid="emeal-report-end" />
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => exportFile("csv")} data-testid="emeal-export-csv"><Download size={15} /> CSV</Button>
          <Button variant="outline" onClick={() => exportFile("xlsx")} data-testid="emeal-export-xlsx"><Download size={15} /> Excel</Button>
        </div>
      </div>

      {!data ? (
        <div className="py-10 text-center text-sm text-ink-mute">{t("Loading…")}</div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Kpi icon={Wallet} label={t("Total Cost")} value={fmtRp(s.total_cost || 0)} testid="emeal-kpi-cost" />
            <Kpi icon={Boxes} label={t("Total Qty")} value={fmtNum(s.total_qty || 0)} testid="emeal-kpi-qty" />
            <Kpi icon={Users} label={t("Employees Fed")} value={fmtNum(s.total_employees || 0)} testid="emeal-kpi-employees" />
          </div>

          {(data.trend || []).length > 0 && (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <Chart title={t("Employee Meal Cost Trend")} icon={TrendingUp}>
                <AreaChart data={data.trend} margin={{ top: 5, right: 8, left: 4, bottom: 0 }}>
                  <defs><linearGradient id="emGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#ea580c" stopOpacity={0.35} /><stop offset="100%" stopColor="#ea580c" stopOpacity={0.02} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1ece6" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 10, fill: "#9b8e82" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#9b8e82" }} width={44} />
                  <Tooltip formatter={(v) => fmtRp(v)} />
                  <Area type="monotone" dataKey="cost" stroke="#ea580c" strokeWidth={2.5} fill="url(#emGrad)" />
                </AreaChart>
              </Chart>
              <Chart title={t("Employee Count Trend")} icon={Users}>
                <BarChart data={data.trend} margin={{ top: 5, right: 8, left: 4, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1ece6" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 10, fill: "#9b8e82" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#9b8e82" }} width={32} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="employees" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </Chart>
            </div>
          )}

          {!branchId && (data.branch_totals || []).length > 1 && (
            <div className="space-y-1.5" data-testid="emeal-branch-totals">
              {data.branch_totals.map((b) => (
                <div key={b.branch_id} className="flex items-center justify-between rounded-lg bg-stone-50 px-3 py-2 text-sm">
                  <span className="flex items-center gap-2 font-semibold text-ink"><Store size={14} className="text-primary" /> {b.branch_name}</span>
                  <span className="text-ink-soft">{b.employees} {t("staff")} · {fmtNum(b.total_qty)} · <b className="text-ink">{fmtRp(b.total_cost)}</b></span>
                </div>
              ))}
            </div>
          )}

          {(data.products || []).length === 0 ? (
            <div className="py-8 text-center text-sm text-ink-mute" data-testid="emeal-report-empty">{t("No employee meals in this period")}</div>
          ) : (
            <div className="max-h-72 overflow-auto rounded-xl border border-line">
              <table className="w-full text-sm" data-testid="emeal-report-table">
                <thead className="sticky top-0 bg-stone-50"><tr className="text-left text-xs uppercase text-ink-mute">
                  <th className="px-3 py-2">{t("Product")}</th>
                  <th className="px-3 py-2 text-right">{t("Quantity")}</th>
                  <th className="px-3 py-2 text-right">{t("Total Cost")}</th></tr></thead>
                <tbody>
                  {data.products.map((p, i) => (
                    <tr key={i} className="border-t border-line/60" data-testid={`emeal-report-row-${i}`}>
                      <td className="px-3 py-2 font-medium text-ink">{p.product_name}</td>
                      <td className="px-3 py-2 text-right">{fmtNum(p.quantity)} {fmtUnit(p.unit)}</td>
                      <td className="px-3 py-2 text-right font-semibold">{fmtRp(p.total_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-xl bg-stone-50 p-3" data-testid={testid}>
      <div className="flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-light text-primary"><Icon size={16} /></span>
        <div className="min-w-0"><p className="truncate text-[11px] font-semibold uppercase text-ink-mute">{label}</p><p className="truncate text-base font-extrabold text-ink">{value}</p></div>
      </div>
    </div>
  );
}

function Chart({ title, icon: Icon, children }) {
  return (
    <div className="rounded-xl border border-line p-3">
      <div className="mb-2 flex items-center gap-2"><Icon size={15} className="text-primary" /><h4 className="text-sm font-bold text-ink">{title}</h4></div>
      <ResponsiveContainer width="100%" height={180}>{children}</ResponsiveContainer>
    </div>
  );
}
